import React, { useEffect, useMemo, useState } from "react";

const DEFAULT_MESSAGES = [
  // "Preparing your classroom...",
  // "Loading courses & lessons...",
  // "Sharpening the pencils...",
  // "Almost ready to learn...",
];

const CHIPS = [
  { icon: "📚", angle: 0 },
  { icon: "🔬", angle: 60 },
  { icon: "🧮", angle: 120 },
  { icon: "🎨", angle: 180 },
  { icon: "💡", angle: 240 },
  { icon: "🌍", angle: 300 },
];

/**
 * EducationLoader
 *
 * Props:
 * - fullScreen  : cover the whole viewport (default true)
 * - title       : heading text
 * - messages    : array of rotating status messages
 * - progress    : number 0-100 for determinate progress; omit for indeterminate
 * - interval    : ms between message changes (default 2200)
 */
export default function EducationLoader({
  fullScreen = true,
  title = "Loading",
  messages = DEFAULT_MESSAGES,
  progress,
  interval = 2500,
}) {
  const [index, setIndex] = useState(0);
  const determinate = typeof progress === "number";
  const pct = determinate ? Math.min(100, Math.max(0, Math.round(progress))) : 0;

  useEffect(() => {
    if (!messages || messages.length < 2) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % messages.length),
      interval
    );
    return () => clearInterval(id);
  }, [messages, interval]);

  // Stable random-ish particles (no re-randomising on every render)
  const particles = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        left: (i * 53) % 100,
        size: 2 + (i % 3),
        delay: (i * 0.7) % 8,
        duration: 7 + (i % 5) * 1.6,
        hue: i % 3 === 0 ? "#67e8f9" : i % 3 === 1 ? "#c084fc" : "#ffffff",
      })),
    []
  );

  return (
    <div
      className={`edu-loader ${fullScreen ? "edu-loader-full" : ""}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={messages?.[index] || title}
    >
      <style>{`
        .edu-loader {
          position: relative;
          isolation: isolate;
          display: grid;
          place-items: center;
          min-height: 520px;
          overflow: hidden;
          color: #e8f0ff;
          font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
          background:
            radial-gradient(ellipse at 50% 40%, #16307a 0%, #0a1030 45%, #030511 100%);
        }
        .edu-loader-full {
          position: fixed;
          inset: 0;
          z-index: 99999;
          width: 100%;
          height: 100vh;
          height: 100dvh;
          min-height: 0;
        }
        .edu-loader *, .edu-loader *::before, .edu-loader *::after { box-sizing: border-box; }

        /* ---------- background layers ---------- */
        .edu-stars {
          position: absolute; inset: 0; z-index: -3; pointer-events: none;
          background-image:
            radial-gradient(2px 2px at 10% 20%, #fff 90%, transparent),
            radial-gradient(2px 2px at 85% 12%, #48eaff 90%, transparent),
            radial-gradient(2px 2px at 78% 75%, #c084fc 90%, transparent),
            radial-gradient(1px 1px at 20% 82%, #fff 90%, transparent),
            radial-gradient(2px 2px at 60% 25%, #67e8f9 90%, transparent),
            radial-gradient(1px 1px at 92% 50%, #fff 90%, transparent),
            radial-gradient(1px 1px at 35% 55%, #fff 90%, transparent),
            radial-gradient(2px 2px at 5% 60%, #a5b4fc 90%, transparent);
          animation: edu-twinkle 4s ease-in-out infinite alternate;
        }
        .edu-grid {
          position: absolute; inset: 0; z-index: -2; opacity: .12; pointer-events: none;
          background-image:
            linear-gradient(#38bdf8 1px, transparent 1px),
            linear-gradient(90deg, #38bdf8 1px, transparent 1px);
          background-size: 38px 38px;
          mask-image: radial-gradient(ellipse at center, #000, transparent 72%);
          -webkit-mask-image: radial-gradient(ellipse at center, #000, transparent 72%);
        }
        .edu-aurora {
          position: absolute; z-index: -2; pointer-events: none;
          width: 70vmax; height: 70vmax; border-radius: 50%;
          filter: blur(80px); opacity: .28;
        }
        .edu-aurora-a { top: -30vmax; left: -20vmax; background: #2563eb; animation: edu-drift 14s ease-in-out infinite alternate; }
        .edu-aurora-b { bottom: -35vmax; right: -25vmax; background: #9333ea; animation: edu-drift 18s ease-in-out infinite alternate-reverse; }

        .edu-particle {
          position: absolute; bottom: -10px; z-index: -1; border-radius: 50%;
          pointer-events: none; opacity: 0;
          animation: edu-rise linear infinite;
        }
        .edu-shooting {
          position: absolute; top: 12%; left: 70%; z-index: -1; width: 110px; height: 2px;
          background: linear-gradient(90deg, #fff, transparent);
          transform: rotate(-35deg); opacity: 0; pointer-events: none;
          animation: edu-shoot 6s ease-in infinite;
        }
        .edu-shooting-two { top: 55%; left: 15%; animation-delay: 3.2s; animation-duration: 7.5s; }

        /* ---------- main content ---------- */
        .edu-content {
          display: flex; flex-direction: column; align-items: center;
          gap: 28px; padding: 24px; text-align: center;
          animation: edu-enter .8s ease both;
        }
        .edu-visual {
          position: relative; display: grid; place-items: center;
          width: min(76vw, 300px); aspect-ratio: 1; perspective: 800px;
        }
        .edu-orbit {
          position: absolute; width: 88%; height: 88%;
          border: 1px solid #42eaff80; border-radius: 50%;
          transform: rotateX(65deg) rotate(-20deg);
          box-shadow: 0 0 15px #22d3ee12;
          animation: edu-orbit 7s linear infinite;
        }
        .edu-orbit::after {
          content: ""; position: absolute; top: 10%; left: 12%;
          width: 9px; height: 9px; border-radius: 50%;
          background: #39f5ff; box-shadow: 0 0 15px #39f5ff;
        }
        .edu-orbit-two { width: 72%; height: 72%; border-color: #d8a4ff90; transform: rotateX(65deg) rotate(35deg); animation-duration: 5s; animation-direction: reverse; }
        .edu-orbit-two::after { background: #d8a4ff; box-shadow: 0 0 15px #d8a4ff; }
        .edu-orbit-three { width: 105%; height: 105%; border-color: #647cff50; animation-duration: 12s; }
        .edu-orbit-three::after { display: none; }

        /* orbiting subject icons */
        .edu-ring {
          position: absolute; inset: 0;
          animation: edu-ring 22s linear infinite;
        }
        .edu-chip {
          position: absolute; top: 50%; left: 50%;
          width: 38px; height: 38px; margin: -19px 0 0 -19px;
          transform: rotate(var(--a)) translateX(calc(min(38vw, 150px) * .98)) rotate(calc(var(--a) * -1));
        }
        .edu-chip > span {
          display: grid; place-items: center; width: 100%; height: 100%;
          font-size: 19px; border-radius: 12px;
          background: #ffffff14; border: 1px solid #ffffff30;
          backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
          box-shadow: 0 0 14px #22d3ee40;
          animation: edu-counter 22s linear infinite, edu-bob 3s ease-in-out infinite;
          animation-delay: 0s, var(--d);
        }

        .edu-core {
          position: relative; z-index: 2; display: grid; place-items: center;
          width: 112px; height: 112px; border: 1px solid #ffffff85; border-radius: 30px;
          background: linear-gradient(140deg, #164eaa, #602bb9 55%, #0e7490);
          box-shadow: 0 0 20px #22d3ee80, 0 0 60px #8b5cf655, inset 0 1px 10px #ffffff45;
          animation: edu-float 2.5s ease-in-out infinite;
        }
        .edu-core::before {
          content: ""; position: absolute; inset: -8px;
          border: 1px solid #a5f3fc50; border-radius: 36px;
          animation: edu-pulse 2.5s ease-out infinite;
        }
        .edu-core::after {
          content: ""; position: absolute; inset: 0; border-radius: inherit;
          background: linear-gradient(115deg, transparent 35%, #ffffff40 50%, transparent 65%);
          background-size: 250% 100%;
          animation: edu-shine 3s ease-in-out infinite;
        }
        .edu-cap { font-size: 54px; line-height: 1; filter: drop-shadow(0 0 12px #67e8f9); }
        .edu-scan {
          position: absolute; z-index: 3; width: 120px; height: 2px; background: #39f5ff;
          box-shadow: 0 0 12px #39f5ff, 0 0 25px #39f5ff;
          animation: edu-scan 2.5s ease-in-out infinite; pointer-events: none;
        }

        /* ---------- text + progress ---------- */
        .edu-title {
          margin: 0; font-size: 13px; font-weight: 700; letter-spacing: .38em;
          text-transform: uppercase; padding-left: .38em;
          background: linear-gradient(90deg, #67e8f9, #c084fc, #67e8f9);
          background-size: 200% 100%;
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
          animation: edu-gradient 4s linear infinite;
        }
        .edu-message {
          margin: 8px 0 0; min-height: 1.5em; font-size: 15px; color: #b6c4ee;
          animation: edu-msg .5s ease both;
        }
        .edu-dots { display: inline-flex; gap: 4px; margin-left: 6px; vertical-align: middle; }
        .edu-dots i { width: 4px; height: 4px; border-radius: 50%; background: #67e8f9; animation: edu-dot 1.2s ease-in-out infinite; }
        .edu-dots i:nth-child(2) { animation-delay: .15s; }
        .edu-dots i:nth-child(3) { animation-delay: .3s; }

        .edu-bar {
          position: relative; width: min(70vw, 280px); height: 6px; overflow: hidden;
          border-radius: 99px; background: #ffffff1a; border: 1px solid #ffffff1f;
        }
        .edu-bar-fill {
          height: 100%; border-radius: inherit;
          background: linear-gradient(90deg, #22d3ee, #818cf8, #c084fc);
          box-shadow: 0 0 12px #22d3ee99;
        }
        .edu-bar-fill.indeterminate { width: 40%; animation: edu-indeterminate 1.6s ease-in-out infinite; }
        .edu-bar-fill.determinate { transition: width .4s ease; position: relative; overflow: hidden; }
        .edu-bar-fill.determinate::after {
          content: ""; position: absolute; inset: 0;
          background: linear-gradient(90deg, transparent, #ffffff70, transparent);
          animation: edu-sweep 1.4s linear infinite;
        }
        .edu-percent { margin-top: -14px; font-size: 12px; color: #8fa3dd; font-variant-numeric: tabular-nums; }

        /* ---------- keyframes ---------- */
        @keyframes edu-orbit { from { rotate: 0deg; } to { rotate: 360deg; } }
        @keyframes edu-ring { to { transform: rotate(360deg); } }
        @keyframes edu-counter { to { rotate: -360deg; } }
        @keyframes edu-bob { 0%,100% { translate: 0 0; } 50% { translate: 0 -4px; } }
        @keyframes edu-float {
          0%,100% { transform: translateY(0) rotateY(-5deg); }
          50% { transform: translateY(-9px) rotateY(5deg); }
        }
        @keyframes edu-pulse { 0% { transform: scale(1); opacity: .8; } 100% { transform: scale(1.35); opacity: 0; } }
        @keyframes edu-shine { 0% { background-position: 150% 0; } 60%,100% { background-position: -50% 0; } }
        @keyframes edu-scan {
          0%,100% { top: 27%; opacity: 0; }
          40%,65% { opacity: .9; }
          85% { top: 73%; opacity: 0; }
        }
        @keyframes edu-twinkle { from { opacity: .35; } to { opacity: .9; } }
        @keyframes edu-drift { from { transform: translate(0,0) scale(1); } to { transform: translate(6vmax, 4vmax) scale(1.15); } }
        @keyframes edu-rise {
          0% { transform: translateY(0); opacity: 0; }
          10% { opacity: .8; }
          90% { opacity: .6; }
          100% { transform: translateY(-110vh); opacity: 0; }
        }
        @keyframes edu-shoot {
          0%,85% { transform: translate(0,0) rotate(-35deg); opacity: 0; }
          88% { opacity: 1; }
          100% { transform: translate(-260px, 180px) rotate(-35deg); opacity: 0; }
        }
        @keyframes edu-enter { from { opacity: 0; transform: translateY(15px) scale(.92); } to { opacity: 1; transform: none; } }
        @keyframes edu-gradient { to { background-position: 200% 0; } }
        @keyframes edu-msg { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        @keyframes edu-dot { 0%,80%,100% { opacity: .25; transform: scale(.8); } 40% { opacity: 1; transform: scale(1.3); } }
        @keyframes edu-indeterminate { 0% { transform: translateX(-110%); } 100% { transform: translateX(280%); } }
        @keyframes edu-sweep { from { transform: translateX(-100%); } to { transform: translateX(100%); } }

        @media (max-width: 480px) {
          .edu-visual { width: 235px; }
          .edu-core { width: 92px; height: 92px; border-radius: 25px; }
          .edu-cap { font-size: 44px; }
          .edu-chip { width: 32px; height: 32px; margin: -16px 0 0 -16px; transform: rotate(var(--a)) translateX(112px) rotate(calc(var(--a) * -1)); }
          .edu-chip > span { font-size: 16px; border-radius: 10px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .edu-loader *, .edu-loader *::before, .edu-loader *::after { animation: none !important; }
          .edu-bar-fill.indeterminate { width: 100%; opacity: .6; }
        }
      `}</style>

      {/* background */}
      <div className="edu-stars" aria-hidden="true" />
      <div className="edu-grid" aria-hidden="true" />
      <div className="edu-aurora edu-aurora-a" aria-hidden="true" />
      <div className="edu-aurora edu-aurora-b" aria-hidden="true" />
      <div className="edu-shooting" aria-hidden="true" />
      <div className="edu-shooting edu-shooting-two" aria-hidden="true" />
      {particles.map((p, i) => (
        <span
          key={i}
          className="edu-particle"
          aria-hidden="true"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: p.hue,
            boxShadow: `0 0 8px ${p.hue}`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}

      {/* content */}
      <div className="edu-content">
        <div className="edu-visual" aria-hidden="true">
          <div className="edu-orbit" />
          <div className="edu-orbit edu-orbit-two" />
          <div className="edu-orbit edu-orbit-three" />

          <div className="edu-ring">
            {CHIPS.map((c, i) => (
              <div key={c.icon} className="edu-chip" style={{ "--a": `${c.angle}deg` }}>
                <span style={{ "--d": `${i * 0.4}s` }}>{c.icon}</span>
              </div>
            ))}
          </div>

          <div className="edu-core">
            <span className="edu-cap">🎓</span>
          </div>
          <div className="edu-scan" />
        </div>

        <div>
          <h2 className="edu-title">{title}</h2>
          <p className="edu-message" key={index}>
            {messages?.[index] ?? ""}
            <span className="edu-dots" aria-hidden="true">
              <i /><i /><i />
            </span>
          </p>
        </div>

        <div
          className="edu-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={determinate ? pct : undefined}
        >
          <div
            className={`edu-bar-fill ${determinate ? "determinate" : "indeterminate"}`}
            style={determinate ? { width: `${pct}%` } : undefined}
          />
        </div>
        {determinate && <div className="edu-percent">{pct}%</div>}
      </div>
    </div>
  );
}